import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';

const formatNumber = (num) => {
  return new Intl.NumberFormat('cs-CZ', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(num || 0);
};

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const REQUEST_TYPE_LABELS = {
  'Tvorba smlouvy': { label: 'Tvorba smlouvy', emoji: '📄' },
  'Technická podpora': { label: 'Technická podpora', emoji: '🛠️' },
  bug: { label: 'Chyba', emoji: '🐛' },
  improvement: { label: 'Vylepšení', emoji: '✨' },
  feature: { label: 'Nová funkce', emoji: '🚀' },
  question: { label: 'Otázka', emoji: '❓' },
  other: { label: 'Ostatní', emoji: '📝' }
};

const STATUS_LABELS = {
  open: 'Otevřený',
  'in-progress': 'V řešení',
  completed: 'Vyřešeno',
  rejected: 'Zamítnuto',
  'needs-revision': 'K revizi',
  'waiting-preparation': 'Čeká na přípravu',
  'contract-created': 'Smlouva vytvořena',
  'documents-sent': 'Dokumenty odeslány',
  'orders-completed': 'Objednávky dokončeny'
};

export default function SupportTab({ userRole, users, currentUserId, isDarkMode }) {
  const [data, setData] = useState([]);
  const [settings, setSettings] = useState({ gpCoefficient: 0.1, requestTypeValues: [] });
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUser, setSelectedUser] = useState(
    userRole === 'podpora' ? currentUserId : 'all'
  );

  const [editRow, setEditRow] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [selectedType, setSelectedType] = useState('all');

  // Historické GP z minulých měsíců
  const [showHistoricalPanel, setShowHistoricalPanel] = useState(false);
  const [historicalPreview, setHistoricalPreview] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalSaved, setHistoricalSaved] = useState([]);
  const [selectedHistoricalIds, setSelectedHistoricalIds] = useState(new Set());
  const [savingHistorical, setSavingHistorical] = useState(false);
  const [manualAssignments, setManualAssignments] = useState({}); // { companyIco: userId }

  // Manuální přidání provize
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({ companyName: '', companyIco: '', assignedTo: '', commission: '', note: '' });
  const [savingManual, setSavingManual] = useState(false);

  // Faktura
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceUserId, setInvoiceUserId] = useState('');
  const [invoiceData, setInvoiceData] = useState(() => {
    try {
      const saved = localStorage.getItem('supportInvoiceData');
      return saved ? JSON.parse(saved) : { supplierName: '', supplierAddress: '', supplierIco: '', supplierDic: '', accountNumber: '', variableSymbol: '', isVatPayer: false };
    } catch { return { supplierName: '', supplierAddress: '', supplierIco: '', supplierDic: '', accountNumber: '', variableSymbol: '', isVatPayer: false }; }
  });

  const isAdmin = userRole === 'admin';

  // Filtrovat dostupné uživatele - podpora vidí jen sebe
  const availableUsers = isAdmin
    ? (users || []).filter(u => u.role === 'podpora' || u.role === 'admin')
    : [];

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/support-commissions?month=${selectedMonth}&userId=${currentUserId || ''}`;
      if (selectedUser && selectedUser !== 'all') {
        url += `&assignedTo=${selectedUser}`;
      }
      const res = await fetch(url);
      if (res.ok) {
        const json = await res.json();
        setData(json.data || []);
        if (json.settings) setSettings(json.settings);
      }
    } catch (err) {
      console.error('Chyba při načítání dat podpory:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistoricalPreview = async () => {
    setHistoricalLoading(true);
    try {
      const res = await fetch(`${API_URL}/support-commissions/historical-gp/preview?month=${selectedMonth}&userId=${currentUserId || ''}`);
      if (res.ok) {
        const json = await res.json();
        const items = json.data || [];
        setHistoricalPreview(items);
        setManualAssignments({});
        setSelectedHistoricalIds(new Set(
          items.filter(i => !i.alreadyCounted).map(i => i.companyIco)
        ));
      }
    } catch (err) {
      console.error('Chyba při načítání historického GP:', err);
    } finally {
      setHistoricalLoading(false);
    }
  };

  const fetchHistoricalSaved = async () => {
    try {
      const res = await fetch(`${API_URL}/support-commissions/historical-gp?month=${selectedMonth}&userId=${currentUserId || ''}`);
      if (res.ok) {
        const json = await res.json();
        setHistoricalSaved(json.data || []);
      }
    } catch (err) {
      console.error('Chyba při načítání uložených historických GP:', err);
    }
  };

  const saveHistoricalToCommissions = async () => {
    setSavingHistorical(true);
    try {
      const toSave = historicalPreview.filter(
        i => selectedHistoricalIds.has(i.companyIco) && !i.alreadyCounted
      ).map(i => ({
        ...i,
        assignedTo: manualAssignments[i.companyIco]
          ? availableUsers.find(u => u._id === manualAssignments[i.companyIco]) || i.assignedTo
          : i.assignedTo
      }));
      const res = await fetch(`${API_URL}/support-commissions/historical-gp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ month: selectedMonth, entries: toSave, userId: currentUserId || '' })
      });
      if (res.ok) {
        await fetchHistoricalSaved();
        alert('Historická GP byla načtena do provizí.');
      } else {
        alert('Chyba při ukládání');
      }
    } catch (err) {
      alert('Chyba: ' + err.message);
    } finally {
      setSavingHistorical(false);
    }
  };

  const saveManualEntry = async () => {
    if (!manualForm.commission || isNaN(parseFloat(manualForm.commission))) {
      alert('Zadejte platnou částku provize');
      return;
    }
    setSavingManual(true);
    try {
      const res = await fetch(`${API_URL}/support-commissions/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          month: selectedMonth,
          companyName: manualForm.companyName,
          companyIco: manualForm.companyIco,
          assignedTo: manualForm.assignedTo || null,
          commission: parseFloat(manualForm.commission),
          note: manualForm.note,
          userId: currentUserId || ''
        })
      });
      if (res.ok) {
        setShowManualModal(false);
        setManualForm({ companyName: '', companyIco: '', assignedTo: '', commission: '', note: '' });
        await fetchHistoricalSaved();
      } else {
        alert('Chyba při ukládání');
      }
    } catch (err) {
      alert('Chyba: ' + err.message);
    } finally {
      setSavingManual(false);
    }
  };

  const deleteManualEntry = async (id) => {
    if (!window.confirm('Smazat tento manuální záznam?')) return;
    try {
      await fetch(`${API_URL}/support-commissions/manual/${id}`, { method: 'DELETE' });
      await fetchHistoricalSaved();
    } catch (err) {
      alert('Chyba: ' + err.message);
    }
  };

  useEffect(() => {
    fetchData();
    fetchHistoricalSaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedUser]);

  // Smazání záznamu (admin only)
  const handleDelete = async (id) => {
    if (!window.confirm('Opravdu smazat tento požadavek z výpočtu provizí?')) return;
    // Označíme jako vyloučený - implementace přes API (soft-delete přes isDeleted na požadavku)
    try {
      await fetch(`${API_URL}/requests/${id}`, { method: 'DELETE' });
      fetchData();
    } catch (err) {
      alert('Chyba při mazání: ' + err.message);
    }
  };

  // Uložení editace (admin only)
  const handleEditSave = async () => {
    try {
      await fetch(`${API_URL}/requests/${editRow._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: editValues.companyName,
          companyIco: editValues.companyIco,
          type: editValues.type,
          assignedTo: editValues.assignedTo || undefined
        })
      });
      setEditRow(null);
      fetchData();
    } catch (err) {
      alert('Chyba při ukládání: ' + err.message);
    }
  };

  // Sumarizace
  const filteredData = selectedType === 'all' ? data : data.filter(row => row.type === selectedType);
  const totalGP = filteredData.reduce((s, d) => s + (d.gp || 0), 0);
  const totalCommission = filteredData.reduce((s, d) => s + (d.commission || 0), 0);

  // Celková částka pro fakturu (filtruje dle vybraného uživatele)
  const invoiceBaseAmount = (() => {
    const uid = invoiceUserId || String(currentUserId);
    const fromMain = filteredData
      .filter(r => String(r.assignedTo?._id || r.assignedTo) === uid)
      .reduce((s, d) => s + (d.commission || 0), 0);
    const fromHistorical = historicalSaved
      .filter(r => String(r.assignedTo?._id || r.assignedTo) === uid)
      .reduce((s, d) => s + (d.commission || 0), 0);
    return fromMain + fromHistorical;
  })();

  const generateSupportQRCode = () => {
    const payoutAmount = invoiceBaseAmount;
    if (!invoiceData.accountNumber || !payoutAmount) return '';
    const baseAmount = Math.round(payoutAmount * 100) / 100;
    const vatAmount = invoiceData.isVatPayer
      ? Math.round(baseAmount * 0.21 * 100) / 100
      : 0;
    const qrAmount = invoiceData.isVatPayer
      ? Math.round((baseAmount + vatAmount) * 100) / 100
      : baseAmount;
    let accountInput = invoiceData.accountNumber.trim().replace(/\s/g, '');
    let iban = '';
    if (!accountInput.startsWith('CZ')) {
      const parts = accountInput.split('/');
      if (parts.length === 2) {
        const bankCode = parts[1].padStart(4, '0');
        const accountParts = parts[0].split('-');
        let prefix = '', accountNumber = '';
        if (accountParts.length === 2) {
          prefix = accountParts[0].padStart(6, '0');
          accountNumber = accountParts[1].padStart(10, '0');
        } else {
          prefix = '000000';
          accountNumber = accountParts[0].padStart(10, '0');
        }
        const bban = bankCode + prefix + accountNumber;
        const ibanCheck = bban + '123500';
        let remainder = 0;
        for (let i = 0; i < ibanCheck.length; i++) {
          remainder = (remainder * 10 + parseInt(ibanCheck[i])) % 97;
        }
        const checksum = (98 - remainder).toString().padStart(2, '0');
        iban = 'CZ' + checksum + bban;
      } else {
        iban = accountInput;
      }
    } else {
      iban = accountInput.replace(/\s/g, '');
    }
    const vs = invoiceData.variableSymbol || '';
    const qrData = `SPD*1.0*ACC:${iban}*AM:${qrAmount.toFixed(2)}*CC:CZK*X-VS:${vs}*`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrData)}`;
  };

  const generateSupportInvoice = () => {
    const uid = invoiceUserId || String(currentUserId);
    const userName = availableUsers.find(u => u._id === uid)?.name ||
      (uid === String(currentUserId) ? (users || []).find(u => u._id === uid)?.name : null) || 'Pracovník';
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    const baseAmount = invoiceBaseAmount;
    const qrCodeUrl = generateSupportQRCode();
    const vatAmount = Math.round(baseAmount * 0.21 * 100) / 100;
    const totalAmount = Math.round(baseAmount * 1.21 * 100) / 100;
    const invoiceNumber = `${new Date().getFullYear()}${String(new Date().getMonth() + 1).padStart(2,'0')}${String(new Date().getDate()).padStart(2,'0')}`;
    const dueDate = new Date(); dueDate.setDate(dueDate.getDate() + 30);
    const fmt = (n) => new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
    const [suppY, suppM] = selectedMonth.split('-');
    const duzpDate = new Date(Number(suppY), Number(suppM), 0).toLocaleDateString('cs-CZ');

    const html = `<html><head><meta charset="utf-8"><title>Faktura - Podpora ${monthName}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { font-family: Arial, sans-serif; margin: 0; padding: 0; font-size: 10px; }
        .header { text-align: center; margin-bottom: 15px; }
        .header h1 { font-size: 20px; margin: 5px 0; }
        .header p { font-size: 9px; margin: 2px 0; }
        .company-info { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 9px; }
        .company-info div { width: 48%; }
        .company-info h3 { font-size: 11px; margin: 0 0 5px 0; }
        .company-info p { margin: 2px 0; line-height: 1.3; }
        .items { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 9px; }
        .items th { border: 1px solid #ddd; padding: 4px; background-color: #f2f2f2; text-align: left; font-size: 9px; }
        .items td { border: 1px solid #ddd; padding: 4px; }
        .total { text-align: right; font-weight: bold; font-size: 11px; margin: 10px 0; }
        .total-row { background-color: #f9f9f9; }
        .invoice-details { margin: 10px 0; font-size: 9px; }
        .qr-code { text-align: center; margin-top: 10px; }
        .qr-code p { font-size: 9px; margin: 5px 0; }
      </style>
    </head><body>
      <div class="header">
        <h1>FAKTURA</h1>
        <p>&#268;&#237;slo faktury: ${invoiceNumber}</p>
        <p>Datum vystavení: ${new Date().toLocaleDateString('cs-CZ')}</p>
        <p>Datum splatnosti: ${dueDate.toLocaleDateString('cs-CZ')}</p>
        ${invoiceData.isVatPayer ? `<p>DUZP: ${duzpDate}</p>` : ''}
      </div>
      <div class="company-info">
        <div>
          <h3>Dodavatel:</h3>
          <p><strong>${invoiceData.supplierName}</strong></p>
          <p>${invoiceData.supplierAddress}</p>
          <p>&#268;&#237;slo ú&#269;tu: ${invoiceData.accountNumber}</p>
          <p>I&#268;O: ${invoiceData.supplierIco}</p>
          ${invoiceData.supplierDic ? `<p>DI&#268;: ${invoiceData.supplierDic}</p>` : ''}
          <p>${invoiceData.isVatPayer ? '<strong>Plátce DPH</strong>' : '<strong>Neplátce DPH</strong>'}</p>
        </div>
        <div>
          <h3>Odběratel:</h3>
          <p><strong>Vyjednáme, s.r.o.</strong></p>
          <p>P&#345;&#237;&#269;ná 1892/4, Nové Město (Praha 1)</p>
          <p>110 00 Praha</p>
          <p>I&#268;O: 21377596</p>
          <p>DI&#268;: CZ21377596</p>
        </div>
      </div>
      <table class="items">
        <thead><tr><th style="width:80%;">Popis služby</th><th style="width:20%;text-align:right;">&#268;ástka</th></tr></thead>
        <tbody>
          <tr>
            <td style="font-size:10px;padding:6px;">Služby externí podpory backoffice &mdash; měsíce ${monthName}</td>
            <td style="font-size:10px;padding:6px;text-align:right;">${fmt(baseAmount)} K&#269;</td>
          </tr>
          ${invoiceData.isVatPayer ? `<tr class="total-row"><td style="text-align:right;font-weight:bold;font-size:10px;padding:6px;">DPH 21%:</td><td style="text-align:right;font-weight:bold;font-size:10px;padding:6px;">${fmt(vatAmount)} K&#269;</td></tr>` : ''}
          <tr class="total-row">
            <td style="text-align:right;font-weight:bold;font-size:11px;padding:6px;">Celkem ${invoiceData.isVatPayer ? 's DPH' : ''}:</td>
            <td style="text-align:right;font-weight:bold;font-size:11px;padding:6px;">${fmt(invoiceData.isVatPayer ? totalAmount : baseAmount)} K&#269;</td>
          </tr>
        </tbody>
      </table>
      <div class="total"><p>Celková částka k úhradě: <strong>${fmt(invoiceData.isVatPayer ? totalAmount : baseAmount)} K&#269;</strong></p></div>
      <div class="invoice-details">
        <p><strong>Platební údaje:</strong></p>
        <p>&#268;&#237;slo ú&#269;tu: ${invoiceData.accountNumber}</p>
        <p>Variabilní symbol: ${invoiceData.variableSymbol || invoiceNumber}</p>
      </div>
      ${qrCodeUrl ? `<div class="qr-code"><p><strong>QR kód pro platbu:</strong></p><img src="${qrCodeUrl}" alt="QR kód pro platbu" /></div>` : ''}
    </body></html>`;

    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };


  // Seskupení dle zpracovatele pro souhrn
  const byUser = {};
  filteredData.forEach(row => {
    const uid = String(row.assignedTo?._id || row.assignedTo);
    const name = row.assignedTo?.name || 'Neznámý';
    if (!byUser[uid]) {
      byUser[uid] = {
        name,
        count: 0,
        technicalSupportCount: 0,
        contractCreationCount: 0,
        gp: 0,
        commission: 0
      };
    }
    byUser[uid].count++;
    if (row.type === 'Technická podpora') byUser[uid].technicalSupportCount++;
    if (row.type === 'Tvorba smlouvy') byUser[uid].contractCreationCount++;
    byUser[uid].gp += row.gp || 0;
    byUser[uid].commission += row.commission || 0;
  });

  const totalTechnicalSupportCount = data.filter(row => row.type === 'Technická podpora').length;
  const totalContractCreationCount = data.filter(row => row.type === 'Tvorba smlouvy').length;

  // Historická GP – seskupení dle assignedTo
  const historicalByUser = {};
  historicalSaved.forEach(row => {
    const uid = String(row.assignedTo?._id || row.assignedTo || 'unknown');
    const name = row.assignedTo?.name || 'Neznámý';
    if (!historicalByUser[uid]) historicalByUser[uid] = { name, count: 0, commission: 0 };
    historicalByUser[uid].count++;
    historicalByUser[uid].commission += row.commission || 0;
  });
  const totalHistoricalCommission = historicalSaved.reduce((s, d) => s + (d.commission || 0), 0);

  const inputCls = `px-2 py-1 rounded border text-sm ${
    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
  }`;

  return (
    <div>
      {/* Filtry */}
      <div className={`mb-5 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} flex flex-wrap gap-4 items-end`}>
        {/* Měsíc */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Měsíc</label>
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            className={inputCls}
          >
            {Array.from({ length: 24 }, (_, i) => {
              const now = new Date();
              const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
              const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
              return (
                <option key={val} value={val}>
                  {d.toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                </option>
              );
            })}
          </select>
        </div>

        {/* Typ požadavku */}
        <div>
          <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Typ požadavku</label>
          <select
            value={selectedType}
            onChange={e => setSelectedType(e.target.value)}
            className={inputCls}
          >
            <option value="all">Všechny typy</option>
            <option value="Tvorba smlouvy">📄 Tvorba smlouvy</option>
            <option value="Technická podpora">🛠️ Technická podpora</option>
          </select>
        </div>

        {/* Uživatel (jen admin) */}
        {isAdmin && (
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Zpracoval</label>
            <select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              className={inputCls}
            >
              <option value="all">Všichni</option>
              {availableUsers.map(u => (
                <option key={u._id} value={u._id}>{u.name}</option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={fetchData}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50"
        >
          {loading ? 'Načítám...' : '🔄 Obnovit'}
        </button>

        <button
          onClick={() => {
            setShowHistoricalPanel(v => !v);
            if (!showHistoricalPanel && isAdmin) fetchHistoricalPreview();
          }}
          className={`px-4 py-2 rounded text-sm text-white ${showHistoricalPanel ? 'bg-purple-700' : 'bg-purple-600 hover:bg-purple-700'}`}
        >
          📊 Aktivace z minulých měsíců
        </button>

        {isAdmin && (
          <button
            onClick={() => setShowManualModal(true)}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
          >
            + Přidat provizi manuálně
          </button>
        )}

        <button
          onClick={() => setShowInvoiceModal(true)}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm"
        >
          📄 Generovat fakturu
        </button>

        <button
          onClick={() => { fetchData(); fetchHistoricalSaved(); }}
          className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          title="Aktualizovat data"
        >
          🔄 Aktualizovat
        </button>
      </div>

      {/* Souhrn */}
      {(Object.keys(byUser).length > 0 || Object.keys(historicalByUser).length > 0) && (
        <div className={`mb-5 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
          <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
            Souhrn — {selectedMonth}
          </h4>
          <div className="flex flex-wrap gap-4">
            {[...new Set([...Object.keys(byUser), ...Object.keys(historicalByUser)])]
              .filter(uid => isAdmin || uid === String(currentUserId))
              .map(uid => {
                const u = byUser[uid];
                const hist = historicalByUser[uid];
                const name = u?.name || hist?.name || 'Neznámý';
                return (
                  <div key={uid} className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow text-sm`}>
                    <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{name}</div>
                    {u && (
                      <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                        🛠️ Technická podpora: {u.technicalSupportCount} · 📄 Tvorba smlouvy: {u.contractCreationCount} (celkem {u.count})
                      </div>
                    )}
                    {u && <div className="text-green-500 font-semibold">Provize a aktivace tento měsíc: {formatNumber(u.commission)} Kč</div>}
                    {hist && <div className="text-purple-500 font-semibold">Aktivace z minulých měsíců: {formatNumber(hist.commission)} Kč</div>}
                    {(u || hist) && (
                      <div className={`mt-1 pt-1 border-t font-bold ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-200 text-gray-900'}`}>
                        Celkem: {formatNumber((u?.commission || 0) + (hist?.commission || 0))} Kč
                      </div>
                    )}
                  </div>
                );
              })}
            {isAdmin && (
              <div className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow text-sm border-l-4 border-blue-500`}>
                <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Celkem</div>
                <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  🛠️ Technická podpora: {totalTechnicalSupportCount} · 📄 Tvorba smlouvy: {totalContractCreationCount} (celkem {data.length})
                </div>
                <div className="text-blue-500 font-bold">Provize a aktivace tento měsíc: {formatNumber(totalCommission)} Kč</div>
                {totalHistoricalCommission > 0 && (
                  <div className="text-purple-600 font-bold">Aktivace z minulých měsíců: {formatNumber(totalHistoricalCommission)} Kč</div>
                )}
                <div className={`mt-1 pt-1 border-t font-bold ${isDarkMode ? 'border-gray-600 text-white' : 'border-gray-200 text-gray-900'}`}>
                  Celkem: {formatNumber(totalCommission + totalHistoricalCommission)} Kč
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Manuálně přidané provize – nahoře */}
      {(() => {
        const manualRows = isAdmin
          ? historicalSaved.filter(r => r.isManual)
          : historicalSaved.filter(r => r.isManual && String(r.assignedTo?._id || r.assignedTo) === String(currentUserId));
        if (manualRows.length === 0) return null;
        return (
          <div className={`mb-5 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} shadow`}>
            <div className={`px-5 py-3 border-b text-sm font-semibold ${isDarkMode ? 'border-gray-600 text-gray-200' : 'border-gray-200 text-gray-800'}`}>
              Manuálně přidané provize
            </div>
            <table className="min-w-full text-sm">
              <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  {isAdmin && <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Pracovník</th>}
                  <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Název firmy</th>
                  <th className={`px-4 py-2 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>IČO</th>
                  <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Poznámka</th>
                  <th className={`px-4 py-2 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Provize</th>
                  {isAdmin && <th className="px-4 py-2"></th>}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'}`}>
                {manualRows.map(row => (
                  <tr key={row._id}>
                    {isAdmin && <td className={`px-4 py-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{row.assignedTo?.name || '—'}</td>}
                    <td className={`px-4 py-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{row.companyName || '—'}</td>
                    <td className="px-4 py-2 text-center font-mono text-xs">{row.companyIco || '—'}</td>
                    <td className={`px-4 py-2 text-xs italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{row.note || ''}</td>
                    <td className="px-4 py-2 text-right font-bold text-green-500">{formatNumber(row.commission)} Kč</td>
                    {isAdmin && (
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => deleteManualEntry(row._id)}
                          className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                          title="Smazat"
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Panel: GP z minulých měsíců */}
      {showHistoricalPanel && (
        <div className={`mb-5 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} shadow`}>
          <div className={`flex items-center justify-between px-5 py-3 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📊 GP z minulých měsíců — {selectedMonth}
            </h4>
            <div className="flex gap-2">
              {isAdmin && (
                <button
                  onClick={saveHistoricalToCommissions}
                  disabled={savingHistorical || historicalLoading}
                  className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm disabled:opacity-50"
                >
                  {savingHistorical ? 'Načítám...' : '📥 Načíst do provizí'}
                </button>
              )}
              <button
                onClick={() => setShowHistoricalPanel(false)}
                className={`px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              >
                Zavřít
              </button>
            </div>
          </div>

          {isAdmin ? (
            historicalLoading ? (
              <div className="text-center py-8 text-gray-500">Načítám data...</div>
            ) : historicalPreview.length === 0 ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Žádná data z Hlavní provize pro tento měsíc.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className="px-3 py-2 w-8"></th>
                      <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Název firmy</th>
                      <th className={`px-3 py-2 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>IČO</th>
                      <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Kdo tvořil smlouvu</th>
                      <th className={`px-3 py-2 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Požadavek ze dne</th>
                      <th className={`px-3 py-2 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>GP firmy</th>
                      <th className={`px-3 py-2 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Provize / Stav</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'}`}>
                    {historicalPreview.map(row => (
                      <tr key={row.companyIco} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2 text-center">
                          {row.alreadyCounted ? (
                            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={selectedHistoricalIds.has(row.companyIco)}
                              onChange={e => {
                                const next = new Set(selectedHistoricalIds);
                                if (e.target.checked) next.add(row.companyIco);
                                else next.delete(row.companyIco);
                                setSelectedHistoricalIds(next);
                              }}
                              className="w-4 h-4 accent-purple-600"
                            />
                          )}
                        </td>
                        <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{row.companyName}</td>
                        <td className="px-3 py-2 text-center font-mono text-xs">{row.companyIco}</td>
                        <td className={`px-3 py-2 ${row.assignedTo || manualAssignments[row.companyIco] ? (isDarkMode ? 'text-blue-400' : 'text-blue-600') : ''}`}>
                          {row.assignedTo?.name ? (
                            row.assignedTo.name
                          ) : (
                            <select
                              value={manualAssignments[row.companyIco] || ''}
                              onChange={e => setManualAssignments(prev => ({ ...prev, [row.companyIco]: e.target.value }))}
                              className={`px-2 py-1 rounded border text-xs w-36 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'}`}
                            >
                              <option value="">-- nenalezeno --</option>
                              {availableUsers.map(u => (
                                <option key={u._id} value={u._id}>{u.name}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-center text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {row.contractRequestDate ? new Date(row.contractRequestDate).toLocaleDateString('cs-CZ') : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold ${row.gp > 0 ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {formatNumber(row.gp)} Kč
                        </td>
                        <td className="px-3 py-2 text-right">
                          {row.alreadyCounted ? (
                            <span className={`text-xs italic ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                              bylo již zapčítáno v hlavní tabulce
                            </span>
                          ) : (
                            <span className="font-bold text-purple-500">{formatNumber(row.commission)} Kč</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            (() => {
              const myHistorical = historicalSaved.filter(
                r => !r.isManual && String(r.assignedTo?._id || r.assignedTo) === String(currentUserId)
              );
              return myHistorical.length === 0 ? (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Zatím žádné aktivace z minulých měsíců.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-3 py-2 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Název firmy</th>
                        <th className={`px-3 py-2 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>IČO</th>
                        <th className={`px-3 py-2 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Požadavek ze dne</th>
                        <th className={`px-3 py-2 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>GP firmy</th>
                        <th className={`px-3 py-2 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Provize</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'}`}>
                      {myHistorical.map(row => (
                        <tr key={row._id} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                          <td className={`px-3 py-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{row.companyName}</td>
                          <td className="px-3 py-2 text-center font-mono text-xs">{row.companyIco}</td>
                          <td className={`px-3 py-2 text-center text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {row.contractRequestDate ? new Date(row.contractRequestDate).toLocaleDateString('cs-CZ') : '—'}
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${row.gp > 0 ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {formatNumber(row.gp)} Kč
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-purple-500">{formatNumber(row.commission)} Kč</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <td colSpan={3} className={`px-3 py-2 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Celkem ({myHistorical.length} aktivací)
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-green-500">
                          {formatNumber(myHistorical.reduce((s, r) => s + (r.gp || 0), 0))} Kč
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-purple-500">
                          {formatNumber(myHistorical.reduce((s, r) => s + (r.commission || 0), 0))} Kč
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* Tabulka */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Načítám data...</div>
      ) : filteredData.length === 0 ? (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Žádné požadavky pro vybraný měsíc a uživatele.
        </div>
      ) : (
        <div className={`rounded-lg shadow overflow-x-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Zpracoval</th>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Typ požadavku</th>
                <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Název firmy</th>
                <th className={`px-4 py-3 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>IČO</th>
                <th className={`px-4 py-3 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Požadavek ze dne</th>
                <th className={`px-4 py-3 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>GP firmy</th>
                <th className={`px-4 py-3 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Jednorázová odměna</th>
                <th className={`px-4 py-3 text-right text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Provize</th>
                {isAdmin && <th className={`px-4 py-3 text-center text-xs font-medium uppercase ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>Akce</th>}
              </tr>
            </thead>
            <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700 bg-gray-800' : 'divide-gray-200 bg-white'}`}>
              {filteredData.map(row => {
                const typeInfo = REQUEST_TYPE_LABELS[row.type] || { label: row.type, emoji: '📝' };
                const isEditing = editRow?._id === row._id;

                return (
                  <tr key={row._id} className={isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className={`px-4 py-3 font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      {isEditing ? (
                        <select
                          value={editValues.assignedTo}
                          onChange={e => setEditValues({ ...editValues, assignedTo: e.target.value })}
                          className={inputCls}
                        >
                          <option value="">— vyberte —</option>
                          {(availableUsers || []).map(u => (
                            <option key={u._id} value={u._id}>{u.name}</option>
                          ))}
                        </select>
                      ) : (
                        row.assignedTo?.name || '—'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <select
                          value={editValues.type}
                          onChange={e => setEditValues({ ...editValues, type: e.target.value })}
                          className={inputCls}
                        >
                          {Object.entries(REQUEST_TYPE_LABELS).map(([k, v]) => (
                            <option key={k} value={k}>{v.emoji} {v.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                          {typeInfo.emoji} {typeInfo.label}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.companyName}
                          onChange={e => setEditValues({ ...editValues, companyName: e.target.value })}
                          className={`${inputCls} w-40`}
                        />
                      ) : (
                        <span className={isDarkMode ? 'text-gray-200' : 'text-gray-900'}>
                          {row.companyName || <span className="text-gray-400 italic">bez firmy</span>}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center font-mono text-xs">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValues.companyIco}
                          onChange={e => setEditValues({ ...editValues, companyIco: e.target.value })}
                          className={`${inputCls} w-28`}
                        />
                      ) : (
                        <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{row.companyIco || '—'}</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-center text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString('cs-CZ') : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.gpDisabledForType ? (
                        <span className={`text-xs italic ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>nezahrnuto</span>
                      ) : row.gpAlreadyCounted ? (
                        <span className={`text-xs italic ${isDarkMode ? 'text-yellow-500' : 'text-yellow-600'}`} title="GP této firmy bylo napočítáno v jiném požadavku">⚠️ počítáno jinde</span>
                      ) : (
                        <span className={`font-semibold ${row.gp > 0 ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {formatNumber(row.gp)} Kč
                        </span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${row.requestValue > 0 ? 'text-orange-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {formatNumber(row.requestValue || 0)} Kč
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-blue-500">
                      {formatNumber(row.commission)} Kč
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3 text-center">
                        {isEditing ? (
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={handleEditSave}
                              className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs"
                            >
                              Uložit
                            </button>
                            <button
                              onClick={() => setEditRow(null)}
                              className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded text-xs"
                            >
                              Zrušit
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => {
                                setEditRow(row);
                                setEditValues({
                                  companyName: row.companyName || '',
                                  companyIco: row.companyIco || '',
                                  type: row.type || 'other',
                                  assignedTo: String(row.assignedTo?._id || row.assignedTo || '')
                                });
                              }}
                              className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                              title="Upravit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDelete(row._id)}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                              title="Smazat z výpočtu"
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {/* Footer součty */}
            <tfoot className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <td colSpan={isAdmin ? 5 : 5} className={`px-4 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Celkem ({filteredData.length} požadavků)</td>
                <td className="px-4 py-3 text-right font-bold text-green-500">{formatNumber(totalGP)} Kč</td>
                <td className="px-4 py-3 text-right font-bold text-orange-500">{formatNumber(filteredData.reduce((s, d) => s + (d.requestValue || 0), 0))} Kč</td>
                <td className="px-4 py-3 text-right font-bold text-blue-500">{formatNumber(totalCommission)} Kč</td>
                {isAdmin && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Info o nastavení koeficientů */}
      <div className={`mt-4 p-3 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500'} text-xs`}>
        Koeficient GP: <strong>{((settings.gpCoefficient || 0) * 100).toFixed(1)} %</strong>
        {' · '}Provize = GP firmy × {((settings.gpCoefficient || 0) * 100).toFixed(1)}% + jednorázová odměna dle typu
      </div>



      {/* Modal: Manuální přidání provize */}
      {showManualModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`rounded-xl shadow-2xl w-full max-w-md p-6 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
            <h3 className="text-lg font-bold mb-4">+ Přidat provizi manuálně</h3>
            <div className="space-y-3">
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pracovník *</label>
                <select
                  value={manualForm.assignedTo}
                  onChange={e => setManualForm(f => ({ ...f, assignedTo: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                >
                  <option value="">-- vyberte pracovníka --</option>
                  {availableUsers.map(u => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Název firmy</label>
                <input
                  type="text"
                  value={manualForm.companyName}
                  onChange={e => setManualForm(f => ({ ...f, companyName: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="nepovinné"
                />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>IČO</label>
                <input
                  type="text"
                  value={manualForm.companyIco}
                  onChange={e => setManualForm(f => ({ ...f, companyIco: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="nepovinné"
                />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Provize (Kč) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={manualForm.commission}
                  onChange={e => setManualForm(f => ({ ...f, commission: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="0"
                />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Poznámka</label>
                <input
                  type="text"
                  value={manualForm.note}
                  onChange={e => setManualForm(f => ({ ...f, note: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="nepovinné"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5 justify-end">
              <button
                onClick={() => setShowManualModal(false)}
                className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                Zrušit
              </button>
              <button
                onClick={saveManualEntry}
                disabled={savingManual}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50"
              >
                {savingManual ? 'Ukládám...' : 'Uložit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Generátor faktury */}
      {showInvoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`rounded-xl shadow-2xl w-full max-w-lg p-6 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} max-h-[90vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">📄 Generátor faktury — Podpora</h3>
              <button onClick={() => setShowInvoiceModal(false)} className={isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>✕</button>
            </div>

            <div className="space-y-3">
              {isAdmin && (
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Pracovník</label>
                  <select
                    value={invoiceUserId}
                    onChange={e => setInvoiceUserId(e.target.value)}
                    className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  >
                    <option value="">-- vyberte pracovníka --</option>
                    {availableUsers.map(u => (
                      <option key={u._id} value={u._id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Název dodavatele *</label>
                <input type="text" value={invoiceData.supplierName}
                  onChange={e => setInvoiceData(d => ({ ...d, supplierName: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Vaše jméno nebo název firmy" />
              </div>
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Adresa dodavatele *</label>
                <input type="text" value={invoiceData.supplierAddress}
                  onChange={e => setInvoiceData(d => ({ ...d, supplierAddress: e.target.value }))}
                  className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                  placeholder="Vaše adresa" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>IČO *</label>
                  <input type="text" value={invoiceData.supplierIco}
                    onChange={e => setInvoiceData(d => ({ ...d, supplierIco: e.target.value }))}
                    className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Vaše IČO" />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>DIČ</label>
                  <input type="text" value={invoiceData.supplierDic}
                    onChange={e => setInvoiceData(d => ({ ...d, supplierDic: e.target.value }))}
                    className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Volitelné" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Číslo účtu *</label>
                  <input type="text" value={invoiceData.accountNumber}
                    onChange={e => setInvoiceData(d => ({ ...d, accountNumber: e.target.value }))}
                    className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Číslo účtu" />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Variabilní symbol</label>
                  <input type="text" value={invoiceData.variableSymbol}
                    onChange={e => setInvoiceData(d => ({ ...d, variableSymbol: e.target.value }))}
                    className={`w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                    placeholder="Volitelné" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={invoiceData.isVatPayer}
                  onChange={e => setInvoiceData(d => ({ ...d, isVatPayer: e.target.checked }))}
                  className="w-4 h-4" />
                <span className="text-sm">Jsem plátce DPH (přičte 21 %)</span>
              </label>

              {/* Náhled */}
              <div className={`p-3 rounded text-sm ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <p className="font-semibold mb-1">Náhled:</p>
                <p>Měsíc: {new Date(selectedMonth + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}</p>
                <p>Předmět: <em>Služby externí podpory backoffice</em></p>
                <p>Částka bez DPH: <strong>{formatNumber(invoiceBaseAmount)} Kč</strong></p>
                {invoiceData.isVatPayer && (
                  <>
                    <p>DPH 21 %: {formatNumber(Math.round(invoiceBaseAmount * 0.21 * 100) / 100)} Kč</p>
                    <p>Celkem s DPH: <strong>{formatNumber(Math.round(invoiceBaseAmount * 1.21 * 100) / 100)} Kč</strong></p>
                  </>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-5 justify-end">
              <button
                onClick={() => {
                  localStorage.setItem('supportInvoiceData', JSON.stringify(invoiceData));
                  alert('Fakturační údaje uloženy.');
                }}
                className={`px-4 py-2 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
              >
                💾 Uložit údaje
              </button>
              <button
                onClick={generateSupportInvoice}
                disabled={!invoiceData.supplierName || !invoiceData.accountNumber}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-sm disabled:opacity-50"
              >
                📄 Vygenerovat fakturu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
